// The main store used for the Finalize Page

// Helpers
import {DateTime as dt} from "luxon";
import mainMixins from "../../../mainMixins";

// Factories
import finalizeFactory from "../factories/finalizeFactory";

export default {
	namespaced: true,
	state: () => ({
		finalizeState: finalizeFactory(),
		installData: [],
		scheduleTimes: [],
		confirmedDateTime: null,
		installIsScheduled: false,
		dropStatus: {
			connectStatus: null,
			connectStatusDate: null
		},
	}),

	getters: {
		installation (state, getters, rootState) {
			return rootState.Buyflow.receipt.cartData?.addOns?.installation?.base
		},
		isSchedulerAvailable (state, getters) {
			return state.scheduleTimes.length > 0 || getters.isTechDelivered()
		},
		isTechDelivered (state, getters) {
			return getters.installation.length > 0 && getters.installation[0].name.indexOf('Tech') > -1
		},
		hasSelectedSelfInstall (state, getters) {
			return getters.installation.length > 0 && getters.installation[0].name.indexOf('SIK') > -1
		},
		hasSelectedProInstall (state, getters) {
			return !getters['hasSelectedSelfInstall']
		},
		formatInstallDate:(state, getters) => (date = null, time = null) => {
			if (!date) return 'Self Install';

			return dt.fromFormat(date, 'L/d/yyyy').toFormat('DDD, ') + getters.formatInstallTime(time)
		},
		formatInstallTime: (state, getters) => (timeFrame = null) => {
			if (timeFrame === 'All Day') return getters.isTechDelivered? 'Dropped off by certified tech*': 'Shipped to home*';
			if (timeFrame) {
				const time = timeFrame.split('-')
				return dt.fromFormat(`${time[0]}M`, 'hmma').toFormat('t - ') + dt.fromFormat(`${time[1]}M`, 'hmma').toFormat('t')
			}
			return ''
		},
		isSelfInstallEligible (state, getters, rootState, rootGetters) {
			const dropStatus3 = function () {
				return ( // drop status is 3 and date is less than 11 months
					state.dropStatus.connectStatus === '3' &&
					state.dropStatus.connectStatusDate !== null &&
					dt.fromFormat(state.dropStatus.connectStatusDate, 'yyyy-LL-dd') >= dt.now().minus({ months: 11 })
				)
			}
			const ohioEligible = function () { // ohio (nee wow-default)
				return (
					rootGetters['Buyflow/isOhio'] && // is ohio
					!rootGetters['Buyflow/voice/hasSelected']() && // has no voice service selected
					!rootGetters['isPendingDisconnect'] && // is not pending disconnect
					rootState.meta?.marketType?.indexOf('fiber') === -1 // is not fiber market
				)
			}
			const eligible = function () { // other markets
				return (
					!rootGetters['Buyflow/isOhio'] && // is not in ohio
					rootState.meta?.marketType?.indexOf('fiber') === -1 && // is not fiber market
					!rootGetters['Buyflow/internet/isEquipmentSelected']({ name: 'Customer Owned Modem' }) && // has modem lease selected
					dropStatus3() && // has the correct drop status information
					(
						!rootGetters['Buyflow/tv/hasSelectedAService'] || // has no tv service added
						rootGetters['Buyflow/tv/getServiceType'] === 'iptvServices' // unless the tv service is Stream TV
					)
				)
			}

			return (ohioEligible() || eligible())
		},
	},

	mutations: {
		SET_DROP_STATUS (state, payload) {
			state.dropStatus = payload
		},
		UPDATE_SCHEDULE_TIMES (state, payload) {
			state.scheduleTimes = payload
		},
		SET_SCHEDULED_INSTALL (state, payload) {
			state.installIsScheduled = payload.value
		},
		SET_CONFIRMED_DATETIME(state, payload) {
			state.confirmedDateTime = payload
		},
		SET_FINALIZE_STATE(state, payload) {
			state.finalizeState = {
				...state.finalizeState,
				...payload
			}
		},
	},

	actions: {
		setFinalizeState({ commit }, payload) {
			commit('SET_FINALIZE_STATE', payload)
		},
		setConfirmedDatetime({ commit }, payload) {
			commit('SET_CONFIRMED_DATETIME', payload)
		},
		fetchDropStatus ({ dispatch, commit }) {
			return dispatch('Buyflow/_fetchDropStatus', null, ROOT)
				.then(response => {
					commit('SET_DROP_STATUS', response.data)
				})
				.catch(e => {
					console.error(e)
				})
		},
		submitCustomerInformation({ state, dispatch, commit, getters, rootGetters }, payload) {
			return dispatch('Buyflow/_checkout_submitCustomerInformation', {
				firstName: payload.firstName,
				lastName: payload.lastName,
				phoneNumber: payload.phoneNumber,
				emailAddress: payload.emailAddress,
				billingLine1: payload.billingLine1,
				billingLine2: payload.billingLine2,
				billingCity: payload.billingCity,
				billingStateOrProvince: payload.billingStateOrProvince,
				billingPostalCode: payload.billingPostalCode,
				referralCode: payload.referralCode,
				referralCodeUsed: payload.referralCode !== '',
			}, ROOT)
				.then(response => {
					if (response.status === 200) {
						commit('SET_METADATA', {
							firstName: payload.firstName,
							lastName: payload.lastName,
							phoneNumber: payload.phoneNumber,
							emailAddress: payload.emailAddress,
							billingLine1: payload.billingLine1,
							billingLine2: payload.billingLine2,
							billingCity: payload.billingCity,
							billingStateOrProvince: payload.billingStateOrProvince,
							billingPostalCode: payload.billingPostalCode,
							referralCode: payload.referralCode,
							referralCodeUsed: payload.referralCode !== '',
						}, ROOT)
						if(!getters.isSelfInstallEligible && !rootGetters['Buyflow/isWowMarket']) {
							return dispatch('submitInstallationFulfillment', state.installData.proInstall.name)

						} else if(!getters.isSelfInstallEligible && rootGetters['Buyflow/isWowMarket'] && (rootGetters['Buyflow/tv/hasSelectedAService'] || rootGetters['Buyflow/voice/hasSelected']())) {
							return dispatch('submitInstallationFulfillment', state.installData.proInstall.name)

						} else if(!getters.isSelfInstallEligible && rootGetters['Buyflow/isWowMarket'] && !rootGetters['Buyflow/tv/hasSelectedAService'] && !rootGetters['Buyflow/voice/hasSelected']()) {
							return dispatch('submitInstallationFulfillment', state.installData.selfInstallKit.name)
						}
					} else {
						throw new Error('Error: unable to submit customer information.')
					}

					return response
				})
		},
		submitInstallationFulfillment ({ dispatch }, payload) {
			return dispatch('Buyflow/updateDecisions', {
				delay: 0,
				install: {
					decision: payload?.decision,
					selection: { name: payload?.name }
				}
			}, ROOT)
		},
		fetchSchedulerTimes ({ state, dispatch, commit }, {startingDate, autoSelect = false}) {
			return dispatch('Buyflow/_fetchSchedulerTimes', { startingDate }, ROOT)
				.then(response => response.data.data)
				.then(response => {
					const dates = startingDate && state.scheduleTimes?.length? [...state.scheduleTimes, ...response[0]] : response[0]
					commit('UPDATE_SCHEDULE_TIMES', dates)
					if(autoSelect){
						const date = dt.fromFormat(response[0][0].date.split(' ')[0], 'L/d/yyyy').toFormat('yyyy-LL-dd')
						return dispatch('submitScheduleInstall', { date, timeSlot: response[0][0].timeSlots[0] })
					}
				})
				.catch(e => {
					mainMixins.sendEvent('resi: checkout', 'resi: checkout confirmation - error retrieving dates', 'resi: checkout confirmation page - scheduler')
					console.error(e)
					dispatch('togglePopup', {
						type: 'BUYFLOW/ERRORS/RETRIEVE_DATES',
						error: e,
					}, ROOT)
				}).finally(() => {
					dispatch('Buyflow/startSession', null, ROOT)
				})
		},
		clearScheduleTimes ({ commit }) {
			commit('UPDATE_SCHEDULE_TIMES', [])
		},
		submitScheduleInstall ({state, dispatch, commit }, payload) {
			return dispatch('Buyflow/_checkout_submitScheduleInstall', payload, ROOT)
				.then(() => {
					commit('SET_SCHEDULED_INSTALL', { value: true })
				})
				.catch(e => {
					console.error(e)
					dispatch('togglePopup', {
						type: 'COMMON/WARNINGS/UNAVAILABLE_DATE',
						error: e,
				}, ROOT)
					throw new Error(e) // rethrow error to be caught by caller to prevent customers from completing a broken order
				})
				.finally(() => {
					dispatch('Buyflow/startSession', null, ROOT)
				})
		},
		clearScheduledInstall({ commit }) {
			commit('SET_SCHEDULED_INSTALL', { value: false, })
		},
	},
}
